import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import TableManager from './TableManager';
import { useAuth } from '../contexts/AuthContext';

interface TableConfig {
  schema: string;
  tableName: string;
  displayName: string;
  hiddenColumns: string[];
  exists: boolean;
}

interface Domain {
  id: string;
  name: string;
  description: string;
  tables: TableConfig[];
}

interface DomainTablesProps {
  domainId: string;
  currentEnvironment: string;
}

const DomainTables: React.FC<DomainTablesProps> = ({ domainId, currentEnvironment }) => {
  const [domain, setDomain] = useState<Domain | null>(null);
  const [activeTable, setActiveTable] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copyingTable, setCopyingTable] = useState<string | null>(null);
  const { canModifyEnvironment, userRole } = useAuth();

  useEffect(() => {
    const fetchDomainTables = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getDomainTables(domainId);
        setDomain(response);

        // Auto-select the first existing table
        const firstExistingTable = response.tables.find((table: TableConfig) => table.exists);
        if (firstExistingTable) {
          setActiveTable(`${firstExistingTable.schema}.${firstExistingTable.tableName}`);
        } else {
          setActiveTable(null);
        }
      } catch (err) {
        console.error('Error fetching domain tables:', err);
        setError('Failed to load domain tables');
        setDomain(null);
        setActiveTable(null);
      } finally {
        setLoading(false);
      }
    };

    if (domainId) {
      fetchDomainTables();
    }
  }, [domainId, currentEnvironment]);

  const handleTableSelect = (tableKey: string) => {
    setActiveTable(tableKey);
  };

  const getSourceEnvironment = (targetEnv: string): string | null => {
    // Determine which environment to copy from based on hierarchy
    if (targetEnv === 'PROD') return 'TEST';
    if (targetEnv === 'TEST') return 'DEV';
    return null; // Can't copy to DEV (it's the lowest)
  };

  const handleCopyTable = async (table: TableConfig) => {
    const sourceEnv = getSourceEnvironment(currentEnvironment);
    if (!sourceEnv) {
      alert('Cannot copy to DEV environment - it is the source environment');
      return;
    }

    const confirmMessage = `Copy table ${table.displayName} from ${sourceEnv} to ${currentEnvironment}?\n\nThis will:\n- Create the table structure\n- Copy all data\n- Copy constraints and indexes`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setCopyingTable(`${table.schema}.${table.tableName}`);
      setError(null);

      // Call API to copy table
      await apiService.copyTableFromEnvironment(
        table.schema,
        table.tableName,
        sourceEnv,
        currentEnvironment
      );

      alert(`Successfully copied ${table.displayName} from ${sourceEnv} to ${currentEnvironment}`);

      // Refresh the domain tables to show the newly created table
      const response = await apiService.getDomainTables(domainId);
      setDomain(response);

      // Auto-select the newly created table
      setActiveTable(`${table.schema}.${table.tableName}`);
    } catch (err: any) {
      console.error('Error copying table:', err);
      setError(`Failed to copy table: ${err.message || 'Unknown error'}`);
      alert(`Failed to copy table: ${err.message || 'Unknown error'}`);
    } finally {
      setCopyingTable(null);
    }
  };

  if (loading) {
    return <div className="domain-tables-loading">Loading domain tables...</div>;
  }

  if (error) {
    return <div className="domain-tables-error">Error: {error}</div>;
  }

  if (!domain) {
    return <div className="domain-tables-empty">No domain selected</div>;
  }

  const existingTables = domain.tables.filter(table => table.exists);
  const nonExistingTables = domain.tables.filter(table => !table.exists);
  const activeTableConfig = domain.tables.find(table =>
    `${table.schema}.${table.tableName}` === activeTable
  );

  return (
    <div className="domain-tables">
      <div className="domain-header">
        <h2>{domain.name}</h2>
        <p className="domain-description">{domain.description}</p>
      </div>

      {existingTables.length > 0 && (
        <>
          <nav className="table-navigation">
            {existingTables.map(table => (
              <button
                key={`${table.schema}.${table.tableName}`}
                className={`table-tab ${activeTable === `${table.schema}.${table.tableName}` ? 'active' : ''}`}
                onClick={() => handleTableSelect(`${table.schema}.${table.tableName}`)}
              >
                {table.displayName}
              </button>
            ))}
          </nav>

          <div className="table-content">
            {activeTableConfig && activeTableConfig.exists && (
              <TableManager
                domainId={domainId}
                schema={activeTableConfig.schema}
                tableName={activeTableConfig.tableName}
                displayName={activeTableConfig.displayName}
                hiddenColumns={activeTableConfig.hiddenColumns}
                currentEnvironment={currentEnvironment}
              />
            )}
          </div>
        </>
      )}

      {nonExistingTables.length > 0 && (
        <div className="missing-tables">
          <h3>Tables not found in {currentEnvironment} environment:</h3>
          {getSourceEnvironment(currentEnvironment) && (
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>
              These tables can be copied from {getSourceEnvironment(currentEnvironment)} environment
            </p>
          )}
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {nonExistingTables.map(table => {
              const tableKey = `${table.schema}.${table.tableName}`;
              const isCopying = copyingTable === tableKey;
              const sourceEnv = getSourceEnvironment(currentEnvironment);

              // Only Task.Sync.AllEnvs or Global.Administrator can copy tables between environments
              const canCopyTables = userRole === 'Task.Sync.AllEnvs' || userRole === 'Global Administrator';
              const canCopy = sourceEnv && canCopyTables && canModifyEnvironment(domainId, currentEnvironment as any);

              return (
                <li key={tableKey} style={{
                  padding: '12px',
                  marginBottom: '8px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span>
                    <strong>{table.displayName}</strong>
                    <span style={{ color: '#666', marginLeft: '8px' }}>
                      ({table.schema}.{table.tableName})
                    </span>
                  </span>
                  {canCopy && (
                    <button
                      onClick={() => handleCopyTable(table)}
                      disabled={isCopying}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: isCopying ? '#6c757d' : '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: isCopying ? 'not-allowed' : 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      {isCopying ? 'Copying...' : `Copy from ${sourceEnv}`}
                    </button>
                  )}
                  {!canCopy && sourceEnv && (
                    <span style={{ color: '#999', fontSize: '12px', fontStyle: 'italic' }}>
                      No permission to copy
                    </span>
                  )}
                  {!sourceEnv && (
                    <span style={{ color: '#999', fontSize: '12px', fontStyle: 'italic' }}>
                      No source available
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {existingTables.length === 0 && nonExistingTables.length === 0 && (
        <div className="no-tables">No tables configured for this domain</div>
      )}
    </div>
  );
};

export default DomainTables;